import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/authContext';
import { getUserRecipes, getAllStackEntries, getRecipeById } from '../services/api';
import { useCompare } from '../context/CompareContext';
import SignedImage from './SignedImage';
import '../styles/menuSchedulingPopup.css';

const CompareSelectionModal = ({ isOpen, onClose, selectedRecipes = [], onToggleRecipe }) => {
    const { user } = useAuth();
    const { openCompareModal } = useCompare();
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [loading, setLoading] = useState(false);
    const popupRef = useRef(null);

    // Drag handlers
    const handleMouseDown = (e) => {
        // Don't start dragging if clicking on interactive elements
        if (e.target.closest('.menu-scheduling-popup-close') || 
            e.target.closest('.menu-scheduling-button') ||
            e.target.closest('.menu-scheduling-remove-button') ||
            e.target.closest('button') ||
            e.target.closest('a')) {
            return;
        }
        
        // Only allow dragging from header area
        if (!e.target.closest('.menu-scheduling-popup-header')) {
            return;
        }
        
        setIsDragging(true);
        const rect = popupRef.current?.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Constrain to viewport
            const maxX = window.innerWidth - (popupRef.current?.offsetWidth || 400);
            const maxY = window.innerHeight - (popupRef.current?.offsetHeight || 200);
            
            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const handleCancel = () => {
        onClose();
    };

    const handleCompare = async () => {
        if (selectedRecipes.length !== 2) return;
        
        setLoading(true);
        try {
            // Fetch full recipe details with ingredients, prerequisites, and steps
            const [recipe1, recipe2] = await Promise.all([
                getRecipeById(selectedRecipes[0].id),
                getRecipeById(selectedRecipes[1].id)
            ]);
            
            // Open compare modal with full recipe data
            openCompareModal(recipe1, recipe2);
            onClose();
        } catch (error) {
            console.error('Error fetching recipe details for comparison:', error);
            alert('Failed to load recipe details for comparison');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveRecipe = (recipeId) => {
        const recipe = selectedRecipes.find(r => r.id === recipeId);
        if (recipe && onToggleRecipe) {
            onToggleRecipe(recipe);
        }
    };

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <div 
            className="menu-scheduling-popup"
            ref={popupRef}
            style={{
                position: 'fixed',
                ...(position.x === 0 && position.y === 0 
                    ? { bottom: '24px', right: '24px' }
                    : { left: `${position.x}px`, top: `${position.y}px` }
                ),
                cursor: isDragging ? 'grabbing' : 'default'
            }}
        >
            <div className="menu-scheduling-popup-content">
                {/* Header - Draggable */}
                <div 
                    className="menu-scheduling-popup-header"
                    onMouseDown={handleMouseDown}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div className="menu-scheduling-popup-header-top">
                        <div className="menu-scheduling-popup-title">
                            <span className="font-mono text-sm uppercase tracking-wider">
                                Compare Recipes
                            </span>
                            <span className="menu-scheduling-popup-count">
                                {selectedRecipes.length}/2 selected
                            </span>
                        </div>
                        
                        <button
                            className="menu-scheduling-popup-close"
                            onClick={handleCancel}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="menu-scheduling-popup-body">
                    <div className="menu-scheduling-selected-recipes">
                        {selectedRecipes.length === 0 ? (
                            <div className="menu-scheduling-empty-state">
                                <div className="menu-scheduling-empty-text">
                                    Select recipes from the Explore or My Recipes pages
                                </div>
                                <div className="menu-scheduling-empty-hint">
                                    Click recipe cards to add them for comparison (2 required)
                                </div>
                            </div>
                        ) : (
                            <div className="menu-scheduling-recipe-cards">
                                {selectedRecipes.map((recipe) => (
                                    <div 
                                        key={recipe.id} 
                                        className="menu-scheduling-recipe-card"
                                    >
                                        <div className="menu-scheduling-recipe-card-thumbnail">
                                            <SignedImage
                                                src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                                alt={recipe.name}
                                                className="menu-scheduling-recipe-card-image"
                                                category={recipe.category}
                                            />
                                        </div>
                                        <div className="menu-scheduling-recipe-card-info">
                                            <div className="menu-scheduling-recipe-card-name">{recipe.name}</div>
                                            {recipe.category && (
                                                <div className="menu-scheduling-recipe-card-category">{recipe.category}</div>
                                            )}
                                            {recipe.cooking_time && (
                                                <div className="menu-scheduling-recipe-card-time">
                                                    {recipe.cooking_time} min
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="menu-scheduling-recipe-card-remove"
                                            onClick={() => handleRemoveRecipe(recipe.id)}
                                            aria-label="Remove recipe"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="menu-scheduling-popup-footer">
                    <button
                        className="menu-scheduling-button menu-scheduling-button-secondary"
                        onClick={handleCancel}
                    >
                        CANCEL
                    </button>
                    <button
                        className="menu-scheduling-button menu-scheduling-button-primary"
                        onClick={handleCompare}
                        disabled={selectedRecipes.length !== 2 || loading}
                    >
                        {loading ? 'LOADING...' : `COMPARE ${selectedRecipes.length === 2 ? '(2)' : ''}`}
                    </button>
                </div>
            </div>
        </div>
        , document.body
    );
};

export default CompareSelectionModal;

